'use clinet';

import clsx from 'classnames';
import SearchBold from 'icons/lib/magnifier_fat.svg';
import {
  ChangeEvent,
  FocusEvent,
  KeyboardEvent,
  RefObject,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { Button, TextField } from 'ui';

import { useTranslation } from '@/i18n/client';
import { pushSearchEvent } from '@/modules/google/data-layer/helpers/search-event';
import { useQuerySearchParam } from '@/shared/hooks/use-query-search-param';
import { useCurrentLocale } from '@/shared/utils/client';

import { useSearchSuggestions } from '../../hooks/use-search-suggestions';
import styles from './autocomplete.module.scss';

export function Autocomplete({
  textFieldRef,
}: {
  textFieldRef: RefObject<HTMLInputElement>;
}) {
  const locale = useCurrentLocale();
  const { t } = useTranslation(['header']);
  const {
    suggestions,
    updateSuggestion,
    attributionToken,
    isAttrbutionTokenLoaded,
  } = useSearchSuggestions();
  const { updateQuery } = useQuerySearchParam();
  const [textFieldValue, setTextFieldValue] = useState('');
  const [isTextFieldFocus, setIsTextFieldFocus] = useState(false);
  const [isSuggestionVisible, setIsSuggestionVisible] = useState(false);
  const [selectedSuggestion, setSelectedSuggestion] = useState<number>(-1);
  const [isAcceptedQuery, setIsAcceptedQuery] = useState(false);

  // Toggle visibility of suggestion box
  useEffect(() => {
    if (suggestions.length && isTextFieldFocus) {
      setIsSuggestionVisible(true);
    } else {
      setIsSuggestionVisible(false);
    }
  }, [suggestions, isTextFieldFocus]);

  // Trigger search page route
  useEffect(() => {
    if (isAttrbutionTokenLoaded === false || isAcceptedQuery === false) {
      return;
    }

    if (selectedSuggestion > -1) {
      // trigger update query with attributionToken from completeQuery
      updateQuery(textFieldValue, attributionToken, selectedSuggestion + 1);
    } else {
      updateQuery(textFieldValue);
    }

    setIsAcceptedQuery(false);
  }, [
    attributionToken,
    selectedSuggestion,
    textFieldValue,
    isAttrbutionTokenLoaded,
    isAcceptedQuery,
    updateQuery,
    locale,
  ]);

  const handleTextFieldChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setTextFieldValue(event.target.value);
      setSelectedSuggestion(-1);

      if (['pl-PL', 'cs-CZ', 'sk-SK', 'de-DE'].includes(locale)) {
        updateSuggestion(event.target.value);
      }
    },
    [locale, updateSuggestion]
  );

  const handleTextFieldBlur = useCallback(
    (event: FocusEvent<HTMLInputElement>) => {
      setIsSuggestionVisible(false);
      setTimeout(() => {
        setIsTextFieldFocus(false);
      }, 100);
    },
    []
  );

  const handleTextFieldFocus = useCallback(() => {
    setIsTextFieldFocus(true);

    if (suggestions.length === 0) return;
    setIsSuggestionVisible(true);
  }, [suggestions?.length]);

  const handleTextFieldKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (isSuggestionVisible === false) return;

      if (event.code === 'ArrowDown') {
        event.preventDefault();
        setSelectedSuggestion((prev) =>
          prev < suggestions.length - 1 ? prev + 1 : 0
        );
      } else if (event.code === 'ArrowUp') {
        event.preventDefault();
        setSelectedSuggestion((prev) =>
          prev > 0 ? prev - 1 : suggestions.length - 1
        );
      }
    },
    [isSuggestionVisible, suggestions.length]
  );

  const handleTextFieldKeyUp = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (['NumpadEnter', 'Enter', 13].includes(event.code)) {
        if (suggestions.length && selectedSuggestion > -1) {
          const value = suggestions[selectedSuggestion].suggestion;
          if (value) {
            setTextFieldValue(value);
          }
        }

        pushSearchEvent(textFieldValue);
        setIsAcceptedQuery(true);
      }

      if (['NumpadEnter', 'Enter', 'Escape', 13].includes(event.code)) {
        event.currentTarget.blur();
      }
    },
    [suggestions, textFieldValue, selectedSuggestion]
  );

  const handleButtonSearchClick = useCallback(() => {
    setIsAcceptedQuery(true);

    pushSearchEvent(textFieldValue);
  }, [textFieldValue]);

  const handleSuggestionItemMouseDown = useCallback(
    (item: any, suggestionIndex: number) => {
      const query = item.suggestion;
      setTextFieldValue(query);
      setSelectedSuggestion(suggestionIndex);
      setIsAcceptedQuery(true);
    },
    []
  );

  const handleNonVertextFieldChange = (
    event: ChangeEvent<HTMLInputElement>
  ) => {
    setTextFieldValue(event.target.value);
  };

  const handleNonVertextFieldKeyUp = (
    event: KeyboardEvent<HTMLInputElement>
  ) => {
    if (['NumpadEnter', 'Enter', 13].includes(event.code)) {
      updateQuery(textFieldValue);
      pushSearchEvent(textFieldValue);
      event.currentTarget.blur();
    }
  };

  return (
    <div
      className={clsx(styles.root, {
        [styles['focued']]: isTextFieldFocus,
      })}
    >
      {['pl-PL', 'cs-CZ', 'sk-SK', 'de-DE'].includes(locale) ? (
        <TextField
          onFocus={handleTextFieldFocus}
          onBlur={handleTextFieldBlur}
          value={textFieldValue}
          onChange={handleTextFieldChange}
          onKeyDown={handleTextFieldKeyDown}
          onKeyUp={handleTextFieldKeyUp}
          fullWidth
          placeholder={t('header.searchPlaceholder') || ''}
          suffix={
            <Button
              size="small"
              variant="iconButton"
              buttonName="textFieldSearchButton"
              onPress={handleButtonSearchClick}
            >
              <SearchBold />
            </Button>
          }
          ref={textFieldRef}
        />
      ) : (
        <TextField
          fullWidth
          onChange={handleNonVertextFieldChange}
          onKeyUp={handleNonVertextFieldKeyUp}
          placeholder={t('header.searchPlaceholder') || ''}
          suffix={
            <Button
              size="small"
              variant="iconButton"
              buttonName="textFieldSearchButton"
              onPress={handleButtonSearchClick}
            >
              <SearchBold />
            </Button>
          }
          ref={textFieldRef}
        />
      )}

      {isSuggestionVisible ? (
        <ul className={styles.suggestions}>
          {suggestions.map((item, index) => (
            <li
              key={index}
              onMouseDown={handleSuggestionItemMouseDown.bind(
                null,
                item,
                index
              )}
              className={clsx(styles.item, {
                [styles['selected']]: index === selectedSuggestion,
              })}
            >
              {item.suggestion}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
